"use client";
import type { GPOnboardingStepProps } from "./utils";

import { AppleLogo } from "@/components/icons/AppleLogo";
import { FacebookLogo } from "@/components/icons/FacebookLogo";
import { GoogleLogo } from "@/components/icons/GoogleLogo";
import { ImmutableLogo } from "@/components/icons/ImmutableLogo";
import { Input } from "@/components/ui/input";
import { type LoginMethod, usePassportProvider } from "@/context";
import { Action, ControlType } from "@/context/AnalyticsProvider";
import { GamePageSection } from "@/context/AnalyticsProvider/gamePage";
import { useLoginModal } from "@/context/LoginModalContext";
import { cn } from "@/lib/utils";
import { notifyError } from "@/utils/monitoring";
import MarketingConsent from "@components/MarketingConsent";
import {
  ArrowRightIcon,
  LoaderCircle,
  MailIcon,
  TriangleAlert,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { z } from "zod";
import { GlassButton } from "../GlassButton";

export const GPOnboardingLogin = ({
  onNext,
  isVisible,
  track,
}: GPOnboardingStepProps) => {
  const loginProgressedRef = useRef(false);
  const [loginMethod, setLoginMethod] = useState<
    "google" | "apple" | "email" | "facebook" | null
  >(null);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);

  const { loginDirectly, isLoggingIn, isLoggedIn } = usePassportProvider();
  const { setDisableLoadingModal } = useLoginModal();

  const handleSocialLogin = async (method: "google" | "apple" | "facebook") => {
    setDisableLoadingModal(true);
    setLoginMethod(method);
    try {
      await loginDirectly(method);
      track({
        action: Action.SUCCEEDED,
        extras: {
          control: `social-login-${method}`.toLowerCase() as Lowercase<string>,
          section: GamePageSection.ONBOARDING_MODAL,
        },
      });
    } catch (error) {
      track({
        action: Action.FAILED,
        extras: {
          control: `social-login-${method}`.toLowerCase() as Lowercase<string>,
          section: GamePageSection.ONBOARDING_MODAL,
        },
      });
      notifyError(error, "login");
    } finally {
      setDisableLoadingModal(false);
    }
  };

  const handleEmailLogin = useCallback(
    async (loginMethod?: LoginMethod) => {
      setDisableLoadingModal(true);
      setLoginMethod("email");
      try {
        await loginDirectly(loginMethod as LoginMethod);
        track({
          action: Action.SUCCEEDED,
          extras: {
            control: "email-login",
            section: GamePageSection.ONBOARDING_MODAL,
          },
        });
      } catch (error) {
        track({
          action: Action.FAILED,
          extras: {
            control: "email-login",
            section: GamePageSection.ONBOARDING_MODAL,
          },
        });
        notifyError(error, "login");
      } finally {
        setDisableLoadingModal(false);
      }
    },
    [loginDirectly, track, setDisableLoadingModal],
  );

  const handleEmailSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!email.trim()) {
        setEmailError("Email is required");
        return;
      }
      if (z.string().email().safeParse(email).error) {
        setEmailError("Invalid email address");
        return;
      }
      setEmailError(null);
      handleEmailLogin({ email });
    },
    [email, handleEmailLogin],
  );

  useEffect(() => {
    if (!isLoggedIn || !isVisible || loginProgressedRef.current) return;
    const timeout = setTimeout(() => {
      loginProgressedRef.current = true;
      onNext();
    }, 1000);
    return () => clearTimeout(timeout);
  }, [isLoggedIn, onNext, isVisible]);

  const handleRulesClick = useCallback(() => {
    track({
      action: Action.PRESSED,
      extras: {
        control: "official-rules",
        controlLabel: "Official Rules",
        controlType: ControlType.LINK,
        section: GamePageSection.ONBOARDING_MODAL,
      },
    });

    window.open("https://www.immutable.com/legal/acceptable-use", "_blank");
  }, [track]);

  const handlePrivacyClick = useCallback(() => {
    track({
      action: Action.PRESSED,
      extras: {
        control: "data-privacy",
        controlLabel: "Data Privacy",
        controlType: ControlType.LINK,
        section: GamePageSection.ONBOARDING_MODAL,
      },
    });

    window.open("https://www.immutable.com/legal/privacy-policy", "_blank");
  }, [track]);

  return (
    <div className="flex h-full w-full flex-col items-center justify-between">
      {/* Progress bar with Motivation text */}
      <div>
        <p className="text-[clamp(1.25rem,6.25vw,1.875rem)] font-bold leading-[clamp(1.5rem,6.25vw,2.25rem)]">
          Become a Pool Champion
        </p>
      </div>
      <div className="relative size-full">
        {/* Logging in */}
        <div
          className={cn(
            "absolute top-1/2 left-1/2 -translate-x-1/2 px-6 py-10 space-y-8 flex flex-col items-center transition-all duration-200 ease-in-out",
            isLoggingIn || isLoggedIn
              ? "opacity-100 -translate-y-1/2 pointer-events-auto"
              : "opacity-0 translate-y-10 pointer-events-none",
          )}
        >
          <LoaderCircle className="size-6 animate-spin" />
          <div className="text-center space-y-2">
            <p className="font-bold">Signing in</p>
            <p className="text-sm text-muted-foreground whitespace-nowrap">
              Continue on <span className="capitalize">{loginMethod}</span>,
              then return.
            </p>
          </div>
        </div>
        {/* Sign in Options */}
        <div
          className={cn(
            "absolute top-1/2 left-1/2 -translate-x-1/2 w-[85%] space-y-5 transition-all duration-200 ease-in-out",
            isLoggingIn || isLoggedIn
              ? "opacity-0 translate-y-10 pointer-events-none"
              : "opacity-100 -translate-y-1/2 pointer-events-auto",
          )}
        >
          <div className="flex items-center gap-x-5">
            <div className="h-px bg-neutral-50/20 flex-grow" />
            <p className="text-sm text-neutral-50">Sign in with Immutable</p>
            <div className="h-px bg-neutral-50/20 flex-grow" />
          </div>

          <div className="flex justify-center gap-x-5 md:gap-x-10">
            <GlassButton
              control="social-login-google"
              section={GamePageSection.ONBOARDING_MODAL}
              className="size-12 md:size-16"
              onClick={() => handleSocialLogin("google")}
            >
              <GoogleLogo className="size-4 md:size-5" />
            </GlassButton>
            <GlassButton
              control="social-login-apple"
              section={GamePageSection.ONBOARDING_MODAL}
              className="size-12 md:size-16"
              onClick={() => handleSocialLogin("apple")}
            >
              <AppleLogo className="size-4 md:size-5" />
            </GlassButton>
            <GlassButton
              control="social-login-facebook"
              section={GamePageSection.ONBOARDING_MODAL}
              className="size-12 md:size-16"
              onClick={() => handleSocialLogin("facebook")}
            >
              <FacebookLogo className="size-4 md:size-5" />
            </GlassButton>
          </div>

          {/*desktop - center */}
          <p className="hidden sm:block text-sm text-neutral-50 text-center">
            or
          </p>
          <div className="flex w-full items-center gap-2 sm:block">
            {/*mobile - left */}
            <p className="text-xs text-neutral-50 sm:hidden">or</p>
            {/* Email Input Field */}
            <form onSubmit={handleEmailSubmit} className="w-full" noValidate>
              <div className="bg-[rgba(243,243,243,0.04)] border border-[rgba(243,243,243,0.16)] rounded-lg flex items-center gap-2 px-2 sm:px-3 py-1 sm:py-2 focus-within:border-[rgba(243,243,243,0.5)] transition-colors">
                <MailIcon className="size-4 sm:size-6 shrink-0 text-muted-foreground" />
                <Input
                  type="email"
                  inputMode="email"
                  placeholder="Enter email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 bg-transparent border-0 px-0 py-0 h-8 sm:h-9 text-sm sm:text-base  text-muted-foreground placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:ring-offset-0"
                  autoComplete="email"
                />
                <button
                  type="submit"
                  className="shrink-0 size-7 sm:size-8 rounded-full bg-[rgba(243,243,243,0.08)] flex items-center justify-center hover:bg-[rgba(243,243,243,0.12)] transition-colors"
                  aria-label="Submit email"
                >
                  <ArrowRightIcon className="size-5 text-[#f3f3f3]" />
                </button>
              </div>
              {emailError && (
                <p className="text-sm h-5 text-red-500 flex items-center gap-x-2 pl-2">
                  <TriangleAlert className="size-4" /> {emailError}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
      {/* Rules & Privacy */}
      <div className="flex items-center gap-1 text-xs sm:text-sm text-muted-foreground">
        <ImmutableLogo className="size-3 mr-1" fill="white" />
        <button
          type="button"
          className="text-muted-foreground underline underline-offset-2 hover:text-white transition-colors"
          onClick={handleRulesClick}
        >
          Official Rules
        </button>
        <span>&</span>
        <button
          type="button"
          className="text-muted-foreground underline underline-offset-2 hover:text-white transition-colors"
          onClick={handlePrivacyClick}
        >
          Data Privacy
        </button>
      </div>
      <MarketingConsent variant="concise" />
    </div>
  );
};
